import type { KnowledgeEvidenceSource, KnowledgeSourceKeys } from "@repo/shared";
import type {
  DocumentEvidenceOwner,
  DocumentEvidencePresentation,
  DocumentEvidenceReference,
} from "@repo/shared/document-evidence-link";
import type {
  DocumentCitationRow,
  EvidenceCitationRow,
  EvidenceFigureImageUrls,
  KnowledgeSourceCitationRow,
} from "./evidence-citations-popover";
import { formatEvidenceLocation } from "./evidence-location";
import type { DocumentEvidenceCitation } from "./evidence-runs";

/**
 * A locator in the shape the `documentEvidence` queries take it: flat, with the span pulled
 * out of `DocumentEvidenceReference`. A reference that cannot address a block never becomes
 * one of these, so every consumer downstream can assume the three identifiers are present.
 */
export interface EvidenceLocatorArgs {
  artifactId: string;
  processingRevisionId: string;
  blockId: string;
  startChar?: number;
  endChar?: number;
  /** Whose citation this is; its carry-over state is presented only to its owner. */
  owner?: DocumentEvidenceOwner;
}

export function buildLocatorArgs(
  reference: DocumentEvidenceReference | null | undefined,
  owner?: DocumentEvidenceOwner | null
): EvidenceLocatorArgs | null {
  if (!reference) return null;
  const locator = buildLocatorArgsFromParts(
    reference.artifactId,
    reference.processingRevisionId,
    reference.blockId,
    reference.span?.startChar,
    reference.span?.endChar
  );
  return locator && owner ? { ...locator, owner } : locator;
}

export function buildLocatorArgsFromParts(
  artifactId: string | undefined,
  processingRevisionId: string | undefined,
  blockId: string | undefined,
  startChar: number | undefined,
  endChar: number | undefined
): EvidenceLocatorArgs | null {
  if (!artifactId || !processingRevisionId || !blockId) return null;
  return {
    artifactId,
    processingRevisionId,
    blockId,
    ...(startChar !== undefined && endChar !== undefined ? { startChar, endChar } : {}),
  };
}

/**
 * Identity of one cited passage — the span included, so two spans of a block stay distinct — as
 * one owner cites it: the same passage cited by an element and by nobody presents differently.
 */
export function buildLocatorKey(locator: EvidenceLocatorArgs): string {
  const owner = locator.owner
    ? locator.owner.kind === "element"
      ? `element:${locator.owner.entityId}`
      : `knowledge:${locator.owner.claimId}`
    : "";
  return [
    locator.artifactId,
    locator.processingRevisionId,
    locator.blockId,
    locator.startChar ?? "",
    locator.endChar ?? "",
    owner,
  ].join("|");
}

/**
 * The reference a citation opens as presented: where the resolver actually read it. A citation
 * carried unchanged into the document's newest version was read there, so its file, its figure
 * and its group are the newest version's; every other citation opens what it cites.
 */
export function presentedReference(
  reference: DocumentEvidenceReference,
  presentation: DocumentEvidencePresentation | undefined
): DocumentEvidenceReference {
  const resolved = presentation?.status === "available" ? presentation.resolvedLocator : undefined;
  if (!resolved) return reference;
  return {
    kind: "document_block",
    artifactId: reference.artifactId,
    processingRevisionId: resolved.processingRevisionId,
    blockId: resolved.blockId,
    ...(resolved.startChar !== undefined && resolved.endChar !== undefined
      ? { span: { startChar: resolved.startChar, endChar: resolved.endChar } }
      : {}),
  };
}

/**
 * What a source-URL request names: the document, and — when the caller has one — the revision
 * that cites it.
 */
export interface EvidenceSourceRequest {
  artifactId: string;
  processingRevisionId?: string;
}

/**
 * Identity of the *file* a citation opens.
 *
 * A revision was parsed from one immutable source version, so the revision is what selects the
 * bytes: two revisions of one document can open two different files, and keying a URL cache on
 * the artifact alone would hand the second citation the first one's link. A request with no
 * revision — the Knowledge screen's already-resolved sources — keys on the artifact alone and
 * means "the document as it is now", which is a different question with a different answer.
 */
export function buildSourceUrlKey(source: EvidenceSourceRequest): string {
  return source.processingRevisionId
    ? `${source.artifactId}|${source.processingRevisionId}`
    : source.artifactId;
}

export function parseSourceUrlKey(key: string): EvidenceSourceRequest {
  const [artifactId, processingRevisionId] = key.split("|");
  return {
    artifactId: artifactId ?? "",
    ...(processingRevisionId ? { processingRevisionId } : {}),
  };
}

/**
 * Identity of the *block* a passage sits in — deliberately span-free. A figure block renders
 * one image no matter how many spans of it are cited, so image URLs are requested, ledgered
 * and stored under this key rather than under the passage key.
 */
export function buildFigureBlockKey(block: {
  artifactId: string;
  processingRevisionId: string;
  blockId: string;
}): string {
  return [block.artifactId, block.processingRevisionId, block.blockId].join("|");
}

/**
 * Splits a drained wanted-set into the limit-sized requests the resolver accepts. Every
 * chunk must be issued in the same effect run: a completed request only updates URL state,
 * which the provider's effects deliberately do not depend on, so a truncated first batch
 * would never be followed by a second and the overflow would sit on its placeholder until
 * the reader closed and reopened the group.
 */
export function chunkForRequest<T>(items: readonly T[], limit: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += limit) {
    chunks.push(items.slice(index, index + limit));
  }
  return chunks;
}

/**
 * Presentations are compared structurally, never by identity: any query layer that hands
 * back a freshly deserialized object on each render would otherwise make the merge in the
 * provider report a change forever and spin it.
 */
export function isSamePresentation(
  a: DocumentEvidencePresentation,
  b: DocumentEvidencePresentation
): boolean {
  if (a === b) return true;
  if (a.status !== b.status) return false;
  if (a.status === "unavailable" || b.status === "unavailable") {
    return a.status === "unavailable" && b.status === "unavailable" && a.label === b.label;
  }
  return (
    a.documentName === b.documentName &&
    // Which action the group offers is read off these two: a body the app draws, the bytes, or
    // nothing typed at all.
    a.fileName === b.fileName &&
    a.mimeType === b.mimeType &&
    a.excerpt === b.excerpt &&
    a.pageStart === b.pageStart &&
    a.pageEnd === b.pageEnd &&
    (a.headingPath ?? []).join("|") === (b.headingPath ?? []).join("|") &&
    // A block that gains, loses or resizes its figure changes what the popover has to lay
    // out; leaving it out here would swallow that update for as long as the entry lives.
    a.figure?.width === b.figure?.width &&
    a.figure?.height === b.figure?.height &&
    // Whether the source is read as a transcript decides which action the group offers, and
    // the index decides which turn it lands on — both have to survive this comparison.
    (a.transcript === undefined) === (b.transcript === undefined) &&
    a.transcript?.blockIndex === b.transcript?.blockIndex &&
    // A refresh of the cited document changes this and nothing else about the passage, so
    // leaving it out would swallow the disclosure for as long as the popover's entry lives.
    a.sourceOutdated === b.sourceOutdated &&
    // A publication or a judgement changes these and nothing else about the passage.
    JSON.stringify(a.carryover) === JSON.stringify(b.carryover) &&
    JSON.stringify(a.resolvedLocator) === JSON.stringify(b.resolvedLocator)
  );
}

/**
 * Collapses one activated run of citations into the rows the popover renders. Marker order
 * is preserved, two markers citing the exact same passage become one row, and a citation
 * whose reference is not a usable locator is dropped.
 *
 * The three resolution channels are read at three different granularities — the presentation
 * by passage, the source URL by cited version, the figure image by block — and each keeps
 * "not answered yet" distinct from "answered with nothing": a passage missing from
 * `presentations` is still loading, a block missing from `figureImageUrls` leaves
 * `figure.urls` null, while a stored entry of two nulls says the image is gone for good.
 *
 * The sources a knowledge item cites are not built here: they arrive already resolved from
 * the server and have no locator to key on. {@link buildKnowledgeSourceRows} is their path.
 */
export function buildCitationRows({
  citations,
  figureImageUrls,
  owner,
  presentations,
  sourceUrls,
}: {
  citations: readonly DocumentEvidenceCitation[];
  figureImageUrls: ReadonlyMap<string, EvidenceFigureImageUrls>;
  /** Whose citations these are; see {@link EvidenceLocatorArgs.owner}. */
  owner?: DocumentEvidenceOwner | null;
  presentations: ReadonlyMap<string, DocumentEvidencePresentation>;
  sourceUrls: ReadonlyMap<string, string | null>;
}): EvidenceCitationRow[] {
  const rows: EvidenceCitationRow[] = [];
  const seen = new Set<string>();

  for (const citation of citations) {
    const cited = buildLocatorArgs(citation.reference, owner);
    if (!cited) continue;
    const key = buildLocatorKey(cited);
    // Two markers citing the exact same passage are one row.
    if (seen.has(key)) continue;
    seen.add(key);

    const resolved = presentations.get(key);
    const availableRow = resolved?.status === "available" ? resolved : null;
    const reference = presentedReference(citation.reference, resolved);
    const locator = buildLocatorArgs(reference) ?? cited;
    const figure = availableRow?.figure;
    rows.push({
      kind: "document",
      key,
      reference,
      documentName:
        availableRow?.documentName ??
        (resolved?.status === "unavailable" ? resolved.label : citation.fallbackLabel),
      ...(availableRow?.fileName ? { fileName: availableRow.fileName } : {}),
      ...(availableRow?.mimeType ? { mimeType: availableRow.mimeType } : {}),
      ...(availableRow?.fileKind ? { fileKind: availableRow.fileKind } : {}),
      excerpt: availableRow?.excerpt ?? null,
      location: availableRow ? formatEvidenceLocation(availableRow) : null,
      status: resolved === undefined ? "loading" : resolved.status,
      ...(availableRow?.sourceOutdated ? { sourceOutdated: true as const } : {}),
      ...(availableRow?.carryover ? { carryover: availableRow.carryover } : {}),
      sourceUrl: sourceUrls.get(buildSourceUrlKey(locator)) ?? null,
      ...(availableRow?.transcript ? { transcript: availableRow.transcript } : {}),
      ...(figure
        ? {
            figure: {
              width: figure.width,
              height: figure.height,
              urls: figureImageUrls.get(buildFigureBlockKey(locator)) ?? null,
            },
          }
        : {}),
    } satisfies DocumentCitationRow);
  }

  return rows;
}

/**
 * What the citing surface knows that the presented sources do not: the item's own text,
 * which is what a hand-written entry quotes, and where a clarification thread can be
 * opened, which needs a route the presenter has no business building.
 */
export interface KnowledgeSourceRowContext {
  statement?: string;
  buildClarificationHref?: (clarificationRequestId: string) => string;
  /** Record ids positionally aligned with the sources, as the presenter returns them. */
  keys?: readonly KnowledgeSourceKeys[];
  /**
   * Openable URLs by `buildSourceUrlKey`, minted while the popover is open. These sources carry
   * no revision, so they key on the artifact alone and open the document as it is now. A source
   * missing from the map has not answered yet and offers no action, which is what keeps a
   * citation from showing a link that goes nowhere.
   */
  sourceUrls?: ReadonlyMap<string, string | null>;
}

/**
 * The rows for a knowledge item whose sources the server already resolved — the Knowledge
 * screen, where there are no evidence chips and nothing to look up on demand.
 *
 * This is the counterpart to {@link buildCitationRows}, which starts from locators and skips
 * document sources for exactly this reason: there, the chips path has already built them.
 */
export function buildKnowledgeSourceRows(
  sources: readonly KnowledgeEvidenceSource[],
  context: KnowledgeSourceRowContext = {}
): EvidenceCitationRow[] {
  return sources.map((source, index) => {
    if (source.kind !== "document") return buildKnowledgeSourceRow(source, index, context);
    const artifactId = context.keys?.[index]?.documentArtifactId;
    return {
      kind: "document",
      key: `knowledge:${index}:document`,
      documentName: source.documentName,
      ...(artifactId ? { artifactId } : {}),
      ...(source.fileName ? { fileName: source.fileName } : {}),
      ...(source.mimeType ? { mimeType: source.mimeType } : {}),
      ...(source.fileKind ? { fileKind: source.fileKind } : {}),
      excerpt: source.excerpt,
      location: formatEvidenceLocation(source),
      ...(source.transcript ? { transcript: source.transcript } : {}),
      ...(source.sourceOutdated ? { sourceOutdated: true as const } : {}),
      ...(source.carryover ? { carryover: source.carryover } : {}),
      // The presenter refuses to return a source it could not re-resolve, so a row that
      // reached here is available by construction.
      status: "available",
      // Minted while the popover is open, like every other citation surface; until the
      // lookup answers the source simply carries no action.
      sourceUrl: (artifactId && context.sourceUrls?.get(buildSourceUrlKey({ artifactId }))) || null,
    } satisfies DocumentCitationRow;
  });
}

const SOURCE_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

function formatSourceTimestamp(timestamp: number): string {
  return Number.isFinite(timestamp) ? SOURCE_TIMESTAMP_FORMATTER.format(new Date(timestamp)) : "";
}

/** Who settled it and when, in one line. The name comes first because it is what a reader scans for. */
function joinAttribution(actor: string, timestamp: number): string {
  const when = formatSourceTimestamp(timestamp);
  return when ? `${actor} · ${when}` : actor;
}

/**
 * Turns one resolved non-document source into a row. Position is the identity: these sources
 * carry no locator, and the presenter already returned them in the order the item cites them.
 */
function buildKnowledgeSourceRow(
  source: Exclude<KnowledgeEvidenceSource, { kind: "document" }>,
  index: number,
  context: KnowledgeSourceRowContext
): KnowledgeSourceCitationRow {
  const key = `knowledge:${index}:${source.kind}`;
  const supportNote = source.supportNote ?? null;
  if (source.kind === "decision") {
    return {
      kind: "decision",
      key,
      heading: source.question,
      answer: source.answer,
      attribution: joinAttribution(`Decided by ${source.decidedByName}`, source.decidedAt),
      supportNote,
    };
  }
  if (source.kind === "clarification") {
    const clarificationRequestId = context.keys?.[index]?.clarificationRequestId;
    return {
      kind: "clarification",
      key,
      heading: source.title,
      answer: source.answer,
      href:
        clarificationRequestId && context.buildClarificationHref
          ? context.buildClarificationHref(clarificationRequestId)
          : null,
      attribution: joinAttribution(
        source.closedByName ? `Closed by ${source.closedByName}` : "Closed",
        source.closedAt
      ),
      supportNote,
    };
  }
  return {
    kind: "manual",
    key,
    heading: null,
    answer: null,
    statement: context.statement ?? null,
    attribution: joinAttribution(source.authorName, source.authoredAt),
    supportNote,
  };
}
